/**
 * BillingDetailsModal — collects state + address + optional GSTIN
 * before a B2C payment so the invoice can be issued GST-compliant.
 *
 * Renders nothing when `open` is false. The host page imperatively
 * opens it via the `useBillingGate` hook before kicking off Cashfree
 * checkout.
 *
 * Props:
 *   open       — boolean
 *   onClose    — () => void
 *   onSaved    — (profile) => void   called after a successful save
 *   purpose    — "topup" | "subscribe" — controls the headline copy
 */
import { useEffect, useState } from "react";
import { X, MapPin, Hash } from "lucide-react";
import { getBillingProfile, upsertBillingProfile } from "../../api/payments.api";

// Indian state codes used as the dropdown source. Two-letter ISO 3166-2:IN
// alpha codes, kept aligned with backend `_ALPHA_TO_NUMERIC`. The display
// name shows alongside the code so the picker is obvious to non-techies.
const IN_STATES = [
  ["AN", "Andaman & Nicobar"],
  ["AP", "Andhra Pradesh"],
  ["AR", "Arunachal Pradesh"],
  ["AS", "Assam"],
  ["BR", "Bihar"],
  ["CH", "Chandigarh"],
  ["CG", "Chhattisgarh"],
  ["DD", "Dadra & Nagar Haveli and Daman & Diu"],
  ["DL", "Delhi"],
  ["GA", "Goa"],
  ["GJ", "Gujarat"],
  ["HR", "Haryana"],
  ["HP", "Himachal Pradesh"],
  ["JK", "Jammu & Kashmir"],
  ["JH", "Jharkhand"],
  ["KA", "Karnataka"],
  ["KL", "Kerala"],
  ["LA", "Ladakh"],
  ["LD", "Lakshadweep"],
  ["MP", "Madhya Pradesh"],
  ["MH", "Maharashtra"],
  ["MN", "Manipur"],
  ["ML", "Meghalaya"],
  ["MZ", "Mizoram"],
  ["NL", "Nagaland"],
  ["OD", "Odisha"],
  ["PY", "Puducherry"],
  ["PB", "Punjab"],
  ["RJ", "Rajasthan"],
  ["SK", "Sikkim"],
  ["TN", "Tamil Nadu"],
  ["TG", "Telangana"],
  ["TR", "Tripura"],
  ["UP", "Uttar Pradesh"],
  ["UK", "Uttarakhand"],
  ["WB", "West Bengal"],
];


export default function BillingDetailsModal({ open, onClose, onSaved, purpose = "subscribe" }) {
  const [profile, setProfile] = useState({
    state_code: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    pincode: "",
    country: "IN",
    gstin: "",
    company_name: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    if (!open) return;
    setError("");
    setWarnings([]);
    setLoading(true);
    getBillingProfile()
      .then((res) => {
        if (res?.data) setProfile((cur) => ({ ...cur, ...res.data }));
      })
      .catch(() => { /* no profile yet — keep defaults */ })
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const update = (k, v) => setProfile((cur) => ({ ...cur, [k]: v }));

  const handleSave = async () => {
    // Bare-minimum requirement: state_code. Without it, place-of-supply
    // defaults to issuer (TN) and accounts will keep emailing the user
    // for the correct state on every invoice. Block save.
    if (!profile.state_code) {
      setError("State is required so we can issue a valid GST invoice.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await upsertBillingProfile(profile);
      setWarnings(res?.data?.warnings || []);
      onSaved?.(res?.data || profile);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
        "Could not save billing details. Please try again."
      );
      setSaving(false);
    }
  };

  const headline = purpose === "topup"
    ? "One last thing — your billing details"
    : "Billing details for your subscription invoice";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-bg-primary border border-border-default rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 pt-5 pb-3 border-b border-border-default">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">{headline}</h2>
            <p className="text-xs text-text-secondary mt-1">
              We issue a GST-valid tax invoice on every payment. Your address
              + state determines whether CGST+SGST or IGST applies.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary p-1 -mr-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3 text-sm">
          {loading ? (
            <p className="text-text-secondary py-6 text-center">Loading…</p>
          ) : (
            <>
              <div>
                <label className="flex items-center gap-1.5 text-xs text-text-secondary mb-1">
                  <MapPin className="w-3.5 h-3.5" />
                  State <span className="text-rose-400">*</span>
                </label>
                <select
                  value={profile.state_code}
                  onChange={(e) => update("state_code", e.target.value)}
                  className="w-full bg-bg-secondary border border-border-default rounded-md px-3 py-2 text-text-primary"
                >
                  <option value="">Select your state…</option>
                  {IN_STATES.map(([code, name]) => (
                    <option key={code} value={code}>{name} ({code})</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs text-text-secondary block mb-1">Address line 1</label>
                  <input
                    type="text" value={profile.address_line_1}
                    onChange={(e) => update("address_line_1", e.target.value)}
                    className="w-full bg-bg-secondary border border-border-default rounded-md px-3 py-2 text-text-primary"
                    placeholder="Building, street"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-text-secondary block mb-1">Address line 2</label>
                  <input
                    type="text" value={profile.address_line_2}
                    onChange={(e) => update("address_line_2", e.target.value)}
                    className="w-full bg-bg-secondary border border-border-default rounded-md px-3 py-2 text-text-primary"
                    placeholder="Locality, landmark"
                  />
                </div>
                <div>
                  <label className="text-xs text-text-secondary block mb-1">City</label>
                  <input
                    type="text" value={profile.city}
                    onChange={(e) => update("city", e.target.value)}
                    className="w-full bg-bg-secondary border border-border-default rounded-md px-3 py-2 text-text-primary"
                  />
                </div>
                <div>
                  <label className="text-xs text-text-secondary block mb-1">PIN code</label>
                  <input
                    type="text" value={profile.pincode}
                    onChange={(e) => update("pincode", e.target.value)}
                    className="w-full bg-bg-secondary border border-border-default rounded-md px-3 py-2 text-text-primary"
                    inputMode="numeric"
                  />
                </div>
              </div>

              <details className="bg-bg-secondary/40 border border-border-default rounded-md px-3 py-2">
                <summary className="cursor-pointer text-xs text-text-secondary flex items-center gap-1.5">
                  <Hash className="w-3.5 h-3.5" />
                  Have a GSTIN? (claim ITC on this invoice)
                </summary>
                <div className="mt-2 space-y-2">
                  <input
                    type="text" value={profile.gstin}
                    onChange={(e) => update("gstin", e.target.value.toUpperCase())}
                    placeholder="15-character GSTIN"
                    className="w-full bg-bg-primary border border-border-default rounded-md px-3 py-2 text-text-primary uppercase"
                    maxLength={15}
                  />
                  <input
                    type="text" value={profile.company_name}
                    onChange={(e) => update("company_name", e.target.value)}
                    placeholder="Company name (as on GST certificate)"
                    className="w-full bg-bg-primary border border-border-default rounded-md px-3 py-2 text-text-primary"
                  />
                </div>
              </details>

              {error && (
                <div className="bg-rose-500/10 border border-rose-500/40 rounded-md p-2.5 text-rose-200 text-xs">
                  {error}
                </div>
              )}
              {warnings.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/40 rounded-md p-2.5 text-amber-200 text-xs">
                  {warnings.join(" · ")}
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-border-default flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading || saving}
            className="px-4 py-1.5 text-sm font-semibold bg-button-primary text-white rounded-md hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save & continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
